import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { useToastMessage } from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useHistory, useLocation } from 'react-router-dom';

import { Path } from '~/routes/constants';

import useApi from './useApi';
import useLocalStorage from './useLocalStorage';
import usePath from './usePath';
import { ApiCommand } from '../Api';
import { Listeners } from '../constants/global.constants';
import localStorageConstants from '../constants/localStorage.constants';
import { OrganizeBy } from '../constants/task.constants';
import urlConstants from '../constants/url.constants';
import { UNASSIGNED_USER } from '../constants/user.constants';
import { useGlobalContext } from '../contexts/GlobalContext';
import { useModeContext, UserMode } from '../contexts/ModeContext';
import {
  FetchTasksResponse,
  getEditedDueDate,
  getTaskParamsFromFilters,
  isValidOrganizeBy,
  mapCompServTaskToTask,
  parseTaskParamFilterValues,
  taskPriorityDefinitions,
  TaskPriorityValue,
  taskStatusDefinitions,
  TaskStatusValue,
} from '../util/task.util';
import { getPrimaryUserOptionText, getUserOption } from '../util/user.util';

const TASKS_BATCH_SIZE = 50;

export type TaskState = {
  createTask: (task: Partial<Task>) => void;
  createTaskData: { taskExternalId: string };
  createTaskError: string;
  isCreateTaskProcessing: boolean;
  isUnsavedAddTaskChanges: boolean;
  isUnsavedEditTaskChanges: boolean;
  resetCreateTaskData: () => void;
  resetCreateTaskError: () => void;
  setIsUnsavedAddTaskChanges: Dispatch<SetStateAction<boolean>>;
  setIsUnsavedEditTaskChanges: Dispatch<SetStateAction<boolean>>;

  editTask: (
    task: Task,
    newStatus?: TaskStatusValue,
    voidReason?: string,
  ) => void;
  editTaskStatusCode: number;
  editTaskError: string;
  resetEditTaskError: () => void;
  isEditTaskLoading: boolean;

  getTasks: (
    params?: Record<string, string | string[]>,
    pageNumber?: number,
    orderBy?: string,
  ) => void;
  tasks: Task[];
  isTasksDataLoading: boolean;
  tasksFetchTimedOut: boolean;
  totalTasksCount: number;

  categories: Category[];

  myOpenTasksCount?: number;

  filters: TTaskFilters;
  resetFiltersToBlank: () => void;
};

const isValidFilterValue = (filterValue: AutoCompleteOption[]): boolean =>
  filterValue && Array.isArray(filterValue);

export const getValidOrganizeByValue = (
  localStorageOrganizeBy: OrganizeBy,
  defaultValue: OrganizeBy,
): OrganizeBy =>
  isValidOrganizeBy(localStorageOrganizeBy)
    ? localStorageOrganizeBy
    : defaultValue;

const MY_OPEN_TASKS_COUNT_POLLING_INTERVAL_SEC = 300;

const useTasks = (): TaskState => {
  const { mode } = useModeContext();
  const { currentUser, listenerCallback } = useGlobalContext();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { createToast } = useToastMessage();

  const {
    storedValue: filterValueLocalStorage,
    setValue: setFilterValueLocalStorage,
  } = useLocalStorage<TaskFiltersInLocalStorage>(
    localStorageConstants.taskFilter,
    {},
  );

  const [tasks, setTasks] = useState<Task[]>([]);
  const [isUnsavedAddTaskChanges, setIsUnsavedAddTaskChanges] =
    useState<boolean>(false);
  const [isUnsavedEditTaskChanges, setIsUnsavedEditTaskChanges] =
    useState<boolean>(false);
  const [sortedCategories, setSortedCategories] = useState<Category[]>([]);
  const [myOpenTasksCount, setMyOpenTasksCount] = useState<
    number | undefined
  >();

  const [totalTasksCount, setTotalTasksCount] = useState<number>(0);

  const { data: categories, sendRequest: fetchCategories } =
    useApi<Category[]>();

  /** Call API endpoint to get task categories */
  const getCategories = (): void => {
    fetchCategories({
      command: ApiCommand.GET,
      url: urlConstants.tasks.categories,
    });
  };

  useEffect(() => {
    if (mode === UserMode.MAUD) getCategories();
  }, [mode]);

  useEffect(() => {
    if (categories?.length) {
      setSortedCategories(
        categories.slice().sort((a, b) => {
          if (a.categoryName < b.categoryName) return -1;
          return a.categoryName > b.categoryName ? 1 : 0;
        }),
      );
    }
  }, [categories?.length]);

  const {
    data: tasksData,
    didTimeOut: tasksFetchTimedOut,
    sendRequest: fetchTasks,
    loading: isTasksDataLoading,
    statusCode: tasksDataStatusCode,
  } = useApi<FetchTasksResponse>();

  const {
    data: tasksBatch,
    sendRequest: fetchTasksBatch,
    statusCode: tasksBatchStatusCode,
  } = useApi<FetchTasksResponse>();

  const { storedValue: organizeByLocalStorage } = useLocalStorage<OrganizeBy>(
    localStorageConstants.taskOrganizeBy,
    OrganizeBy.DUE_DATE,
  );

  /** Call API endpoint to get tasks */
  const getTasks = (
    params: Record<string, string | string[]> = {},
    pageNumber = 1,
    orderBy = getValidOrganizeByValue(
      organizeByLocalStorage,
      OrganizeBy.DUE_DATE,
    ),
  ): void => {
    const options = {
      'filter-by': {},
      'order-by': orderBy,
      'page-number': pageNumber,
      'page-size': TASKS_BATCH_SIZE,
    };

    if (Object.keys(params ?? {}).length) {
      options['filter-by'] = params;
    }

    // Should only return Maud tasks, excluding family tasks
    options['filter-by']['is-family-task'] = false;

    // Use "batch" fetch when fetching additional pages to concat new results, otherwise
    // use normal fetch and replace tasks list with new data
    if (options['page-number'] > 1) {
      fetchTasksBatch({
        command: ApiCommand.GET,
        options,
        url: urlConstants.tasks.listTasks,
      });
    } else {
      fetchTasks({
        command: ApiCommand.GET,
        options,
        url: urlConstants.tasks.listTasks,
      });
    }
  };

  // Update tasks state any time tasks data changes
  useEffect(() => {
    if (tasksDataStatusCode === 200) {
      setTasks(
        (tasksData?.data ?? []).map((task) =>
          mapCompServTaskToTask(task, categories),
        ),
      );
      setTotalTasksCount(tasksData.totalCount);
    }
  }, [categories?.length, tasksDataStatusCode]);

  useEffect(() => {
    if (tasksBatchStatusCode === 200) {
      setTasks((currentTasks) => [
        ...currentTasks,
        ...(tasksBatch?.data ?? []).map((task) =>
          mapCompServTaskToTask(task, categories),
        ),
      ]);
    }
  }, [tasksBatchStatusCode]);

  const { data: taskCountObject, sendRequest: getCount } = useApi<{
    taskCount: number;
  }>();

  const getMyOpenTaskCount = () => {
    getCount({
      command: ApiCommand.GET,
      url: urlConstants.tasks.countForUserId(currentUser.userId),
    });
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (currentUser.userId) getMyOpenTaskCount();
    }, 1000 * MY_OPEN_TASKS_COUNT_POLLING_INTERVAL_SEC);
    return () => clearInterval(intervalId);
  }, []);

  // use a separate state variable to prevent visible refresh when value doesn't change
  useEffect(() => {
    setMyOpenTasksCount(taskCountObject?.taskCount ?? myOpenTasksCount);
  }, [taskCountObject?.taskCount]);

  const {
    data: createTaskData,
    sendRequest: addTask,
    loading: isCreateTaskProcessing,
    error: createTaskError,
    resetData: resetCreateTaskData,
    resetError: resetCreateTaskError,
  } = useApi<{ taskExternalId: string }>();

  /** Call API endpoint to create a task, store new task details */
  const createTask = (task: Partial<Task>): void => {
    const { firstName, lastName, userId } = currentUser;
    addTask({
      command: ApiCommand.POST,
      options: {
        dueDate: DateTime.fromJSDate(new Date(task.dueDate)).toFormat(
          'yyyy-LL-dd',
        ),
        inquiryId: task.inquiryId,
        patientId: task.patient?.userId,
        taskAssignedUserId: task.assignee?.userId,
        taskAssignedUserName: getPrimaryUserOptionText(
          task.assignee ?? UNASSIGNED_USER,
        ),
        taskCategoryExternalId: task.category.categoryExternalId,
        taskCreatedByUserId: userId,
        taskCreatedByUserName: `${firstName} ${lastName}`,
        taskDescription: task.description,
        taskName: task.name,
        taskPriorityId: task.taskPriorityId,
        taskStatusId: '1',
        taskSubCategoryExternalId: task.subcategory?.categoryExternalId,
      },
      url: urlConstants.tasks.post,
    });
  };

  useEffect(() => {
    if (createTaskData?.taskExternalId) {
      listenerCallback(
        [
          Listeners.PATIENTMENU_TASKDETAILS_CREATE,
          Listeners.PATIENTTASKS_TASKDETAILS_CREATE,
        ],
        [[], []],
      );
      getMyOpenTaskCount();
      createToast({
        id: `task-created-toast__${createTaskData.taskExternalId}`,
        text: 'Task added',
        variant: 'success',
        position: 'top',
        button: {
          text: 'View task',
          onClick: () => {
            history.push({
              pathname: `${Path.MAUD_TASKS}/${createTaskData.taskExternalId}`,
              state: {
                fromTaskRoute: location.pathname.includes(Path.MAUD_TASKS),
              },
            });
          },
        },
      });
    }
  }, [createTaskData?.taskExternalId]);

  const getCurrentUserAsAssigneeFilter = () => [
    getUserOption(true)(currentUser),
  ];

  const getValidFilterValue = (
    localStorageFilterValue: AutoCompleteOption[],
    defaultValue: AutoCompleteOption[],
  ): AutoCompleteOption[] =>
    isValidFilterValue(localStorageFilterValue)
      ? localStorageFilterValue
      : defaultValue;

  // filters
  const [selectedAssignees, setSelectedAssignees] = useState<
    AutoCompleteOption[]
  >(
    getValidFilterValue(
      filterValueLocalStorage.assignee,
      getCurrentUserAsAssigneeFilter(),
    ),
  );

  const [selectedPatients, setSelectedPatients] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.patient, []));

  const [selectedCreators, setSelectedCreators] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.creator, []));

  const [selectedCategories, setSelectedCategories] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.category, []));

  const { title: highPriorityTitle } =
    taskPriorityDefinitions[TaskPriorityValue.HIGH];
  const [selectedPriorities, setSelectedPriorities] = useState<
    AutoCompleteOption[]
  >(
    getValidFilterValue(filterValueLocalStorage.priority, [
      { primaryText: highPriorityTitle, value: highPriorityTitle },
    ]),
  );

  const [selectedDueDateFrom, setSelectedDueDateFrom] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.dueDateFrom, []));

  const [selectedDueDateTo, setSelectedDueDateTo] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.dueDateTo, []));

  const [selectedCreatedDateFrom, setSelectedCreatedDateFrom] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.createdDateFrom, []));

  const [selectedCreatedDateTo, setSelectedCreatedDateTo] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.createdDateTo, []));

  const [selectedStatus, setSelectedStatus] = useState<AutoCompleteOption[]>(
    getValidFilterValue(
      filterValueLocalStorage.status,
      taskStatusDefinitions
        .slice(0, 2)
        .map(({ title }) => ({ primaryText: title, value: title })),
    ),
  );

  const [selectedTitleFilter, setSelectedTitleFilter] = useState<
    AutoCompleteOption[]
  >(getValidFilterValue(filterValueLocalStorage.title, []));

  const filters = useMemo<TTaskFilters>(
    () => ({
      assignee: { setter: setSelectedAssignees, value: selectedAssignees },
      category: { setter: setSelectedCategories, value: selectedCategories },
      createdDateFrom: {
        setter: setSelectedCreatedDateFrom,
        value: selectedCreatedDateFrom,
      },
      createdDateTo: {
        setter: setSelectedCreatedDateTo,
        value: selectedCreatedDateTo,
      },
      creator: { setter: setSelectedCreators, value: selectedCreators },
      dueDateFrom: {
        setter: setSelectedDueDateFrom,
        value: selectedDueDateFrom,
      },
      dueDateTo: {
        setter: setSelectedDueDateTo,
        value: selectedDueDateTo,
      },
      patient: { setter: setSelectedPatients, value: selectedPatients },
      priority: { setter: setSelectedPriorities, value: selectedPriorities },
      status: { setter: setSelectedStatus, value: selectedStatus },
      title: { setter: setSelectedTitleFilter, value: selectedTitleFilter },
    }),
    [
      selectedAssignees,
      selectedPatients,
      selectedCategories,
      selectedCreators,
      selectedPriorities,
      selectedDueDateFrom,
      selectedDueDateTo,
      selectedCreatedDateFrom,
      selectedCreatedDateTo,
      selectedStatus,
      selectedTitleFilter,
    ],
  );

  useEffect(() => {
    const filterValue: TaskFiltersInLocalStorage = {};
    Object.keys(filters ?? {}).forEach((key) => {
      filterValue[key] = filters[key].value;
    });
    setFilterValueLocalStorage(filterValue);
  }, [filters]);

  const resetFiltersBase = (): void => {
    filters.patient.setter([]);
    filters.category.setter([]);
    filters.creator.setter([]);
    filters.priority.setter([]);
    filters.dueDateFrom.setter([]);
    filters.dueDateTo.setter([]);
    filters.createdDateFrom.setter([]);
    filters.createdDateTo.setter([]);
    filters.title.setter([]);
  };

  const resetFiltersToBlank = (): void => {
    resetFiltersBase();
    filters.assignee.setter([]);
    filters.status.setter([]);
  };

  const { isChildPath } = usePath();

  useEffect(() => {
    getMyOpenTaskCount(); // initial fetch as soon as currentUser loads
    // if page loads on a task details page, don't fetch current user's tasks
    const isTaskDetails = isChildPath(Path.MAUD_TASKS);
    const isPatientProfile = isChildPath(Path.MAUD_PATIENTS);
    if (!(isTaskDetails || isPatientProfile))
      getTasks(getTaskParamsFromFilters(parseTaskParamFilterValues(filters)));
  }, []);

  useEffect(() => {
    // clear filters when navigating to a tasks route from a non-tasks route
    // so filters persist only between tasks list and task details
    // fetch tasks with the default filters to ensure tasks list matches the filters
    if (
      location.state?.fromTaskRoute !== undefined &&
      location.pathname.includes(Path.MAUD_TASKS)
    ) {
      getTasks(getTaskParamsFromFilters(parseTaskParamFilterValues(filters)));
    }
  }, [location]);

  const {
    sendRequest: updateTask,
    statusCode: editTaskStatusCode,
    error: editTaskError,
    resetError: resetEditTaskError,
    loading: isEditTaskLoading,
  } = useApi<{ status: number }>();

  /** Call API endpoint to edit a task, store edited task details */
  const editTask = (
    task: Task,
    newStatus?: TaskStatusValue,
    voidReasonExternalId?: string,
  ): void => {
    const params: Record<string, string | string[] | boolean> = {};
    params.taskExternalId = task.taskExternalId;
    params.patientId = task.patient?.userId ?? null;
    params.taskAssignedUserId = task.assignee?.userId ?? null;
    params.taskAssignedUserName = getPrimaryUserOptionText(
      task.assignee ?? UNASSIGNED_USER,
    );
    params.taskCategoryExternalId = task.category?.categoryExternalId ?? null;
    params.taskDescription = task.description ?? '';
    params.dueDate = getEditedDueDate(task.dueDate, task.serverDueDate);
    params.taskName = task.name;
    params.taskPriorityId = task.taskPriorityId;
    params.taskStatusId = task.taskStatusId;
    params.voidReasonExternalId =
      voidReasonExternalId || task.voidReasonExternalId || null;
    params.taskSubCategoryExternalId =
      task.subcategory?.categoryExternalId ?? null;
    params.taskUpdatedByUserId = currentUser.userId;
    params.taskUpdatedByUserName = getPrimaryUserOptionText(currentUser);

    if (newStatus) {
      params.taskStatusId = taskStatusDefinitions[newStatus]?.value;
    }

    if (task.isFamilyTask) {
      params.isFamilyTask = true;
      params.familyAssignees = task.familyAssignees.map((a) => a.externalId);
    }

    updateTask({
      command: ApiCommand.PUT,
      options: params,
      url: urlConstants.tasks.put,
    });
  };

  // If update task response is successful, re-fetch tasks and count to ensure data is current
  useEffect(() => {
    if (editTaskStatusCode === 200) {
      getTasks(getTaskParamsFromFilters(parseTaskParamFilterValues(filters)));
      getMyOpenTaskCount();
      listenerCallback(
        [
          Listeners.PATIENTMENU_TASKDETAILS_UPDATE,
          Listeners.PATIENTTASKS_TASKDETAILS_UPDATE,
        ],
        [[], []],
      );
    }
  }, [editTaskStatusCode]);

  return {
    categories: sortedCategories,
    createTask,
    createTaskData,
    createTaskError,
    editTask,
    editTaskError,
    editTaskStatusCode,
    filters,
    getTasks,
    isCreateTaskProcessing,
    isEditTaskLoading,
    isTasksDataLoading,
    isUnsavedAddTaskChanges,
    isUnsavedEditTaskChanges,
    myOpenTasksCount,
    resetCreateTaskData,
    resetCreateTaskError,
    resetEditTaskError,
    resetFiltersToBlank,
    setIsUnsavedAddTaskChanges,
    setIsUnsavedEditTaskChanges,
    tasks,
    tasksFetchTimedOut,
    totalTasksCount,
  };
};

export default useTasks;
